// User Administration — list/create/role/activate/deactivate + offboard (bulk reassign).
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { BulkModal } from "@/components/bulk-modal";
import { api } from "@/lib/api";
import { useAuthStore } from "@/stores/auth-store";

// Mirrors the Prisma UserRole enum (backend authoritative).
const ROLES = [
  "SUPER_ADMIN", "ADMIN", "LEADERSHIP", "SALES_MANAGER", "SALES_REP",
  "STAFFING_MANAGER", "RECRUITER", "GOV_CAPTURE_MANAGER", "PROPOSAL_WRITER",
  "DELIVERY_PM", "FINANCE", "MARKETING", "SDR",
] as const;
const ENTITIES = ["BOTH", "US", "INDIA", "GLOCALIZE"] as const;

interface UserRow {
  id: string;
  name: string;
  email: string;
  role: string;
  entity: string;
  territory: string | null;
  isActive: boolean;
  source: string;
  createdAt: string;
  counts: { accounts: number; contacts: number; deals: number; leads: number; jobOrders: number; candidates: number };
}

const pretty = (r: string) => r.replace(/_/g, " ");
const apiError = (e: any) =>
  e?.response?.data?.error?.message ?? e?.response?.data?.message ?? e?.message ?? "Something went wrong";

function sourceVariant(s: string): "default" | "secondary" | "outline" {
  if (s === "Pre-provisioned") return "outline";
  if (s === "Zoho") return "secondary";
  return "default";
}

export default function AdminUsersPage() {
  const me = useAuthStore((s) => s.me);
  const isAdmin = me?.role === "ADMIN" || me?.role === "SUPER_ADMIN";
  const isSuper = me?.role === "SUPER_ADMIN";
  const qc = useQueryClient();

  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["/users/admin", { status, q }],
    queryFn: async () => (await api.get("/users/admin", { params: { status, q } })).data.data as UserRow[],
    enabled: isAdmin,
  });

  // Active users for the "reassign to" picker.
  const activeUsers = useQuery({
    queryKey: ["/users", "picker"],
    queryFn: async () => (await api.get("/users")).data.data as { id: string; name: string; email: string }[],
    enabled: isAdmin,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["/users/admin"] });
    qc.invalidateQueries({ queryKey: ["/users"] });
  };

  const updateUser = useMutation({
    mutationFn: async (v: { id: string; body: Record<string, unknown> }) => (await api.put(`/users/${v.id}`, v.body)).data,
    onSuccess: () => { setError(null); invalidate(); },
    onError: (e) => setError(apiError(e)),
  });

  // --- Add user modal ---
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ email: "", name: "", role: "SALES_REP", entity: "BOTH", territory: "" });
  const [addError, setAddError] = useState<string | null>(null);
  const createUser = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        email: addForm.email.trim().toLowerCase(),
        name: addForm.name.trim(),
        role: addForm.role,
        entity: addForm.entity,
      };
      if (addForm.territory.trim()) body.territory = addForm.territory.trim();
      return (await api.post("/users", body)).data;
    },
    onSuccess: () => {
      setAddOpen(false);
      setAddForm({ email: "", name: "", role: "SALES_REP", entity: "BOTH", territory: "" });
      setAddError(null);
      invalidate();
    },
    onError: (e) => setAddError(apiError(e)),
  });

  // --- Offboard / reassign modal ---
  const [offboard, setOffboard] = useState<UserRow | null>(null);
  const [toUserId, setToUserId] = useState("");
  const [deactivate, setDeactivate] = useState(true);
  const [revokeTokens, setRevokeTokens] = useState(true);
  const [reassignError, setReassignError] = useState<string | null>(null);
  const reassign = useMutation({
    mutationFn: async () =>
      (await api.post(`/users/${offboard!.id}/reassign`, { toUserId, deactivate, revokeTokens })).data,
    onSuccess: () => { setOffboard(null); setToUserId(""); setReassignError(null); invalidate(); },
    onError: (e) => setReassignError(apiError(e)),
  });

  const reassignTargets = useMemo(
    () => (activeUsers.data ?? []).filter((u) => u.id !== offboard?.id),
    [activeUsers.data, offboard],
  );

  if (!isAdmin) {
    return (
      <>
        <PageHeader title="User Administration" />
        <div className="p-6 text-sm text-muted-foreground">You don't have permission to view this page.</div>
      </>
    );
  }

  const rows = list.data ?? [];

  return (
    <>
      <PageHeader
        title="User Administration"
        description={<Link to="/admin" className="hover:underline">← Back to Admin</Link>}
        actions={<Button onClick={() => { setAddError(null); setAddOpen(true); }}>Add user</Button>}
      />

      <div className="space-y-4 p-6">
        {error && (
          <div className="flex items-center justify-between rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-xs underline">dismiss</button>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-40">
            <option value="all">All users</option>
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
          </Select>
          <Input
            placeholder="Search name or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-64"
          />
          <div className="ml-auto text-xs text-muted-foreground">{rows.length} user{rows.length === 1 ? "" : "s"}</div>
        </div>

        <div className="rounded-md border">
          <Table>
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th>Entity</Th>
                <Th>Status</Th>
                <Th>Source</Th>
                <Th>Owned (A/C/L/D)</Th>
                <Th className="text-right">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {list.isLoading && <Tr><Td colSpan={8} className="py-8 text-center text-muted-foreground">Loading…</Td></Tr>}
              {!list.isLoading && rows.length === 0 && (
                <Tr><Td colSpan={8} className="py-8 text-center text-muted-foreground">No users match.</Td></Tr>
              )}
              {rows.map((u) => {
                const isSelf = u.id === me?.id;
                return (
                  <Tr key={u.id} className={u.isActive ? "" : "opacity-60"}>
                    <Td className="font-medium">{u.name}{isSelf && <span className="ml-1 text-xs text-muted-foreground">(you)</span>}</Td>
                    <Td className="text-muted-foreground">{u.email}</Td>
                    <Td>
                      <Select
                        value={u.role}
                        disabled={updateUser.isPending}
                        onChange={(e) => updateUser.mutate({ id: u.id, body: { role: e.target.value } })}
                        className="h-8 w-44"
                      >
                        {ROLES.map((r) => <option key={r} value={r}>{pretty(r)}</option>)}
                      </Select>
                    </Td>
                    <Td>{u.entity}</Td>
                    <Td>
                      <Badge variant={u.isActive ? "default" : "outline"}>{u.isActive ? "Active" : "Inactive"}</Badge>
                    </Td>
                    <Td><Badge variant={sourceVariant(u.source)}>{u.source}</Badge></Td>
                    <Td className="whitespace-nowrap text-xs text-muted-foreground">
                      {u.counts.accounts}/{u.counts.contacts}/{u.counts.leads}/{u.counts.deals}
                    </Td>
                    <Td className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          className="h-8 px-2 text-xs"
                          disabled={isSelf || updateUser.isPending}
                          onClick={() => updateUser.mutate({ id: u.id, body: { isActive: !u.isActive } })}
                        >
                          {u.isActive ? "Deactivate" : "Activate"}
                        </Button>
                        {isSuper && (
                          <Button
                            variant="destructive"
                            className="h-8 px-2 text-xs"
                            disabled={isSelf}
                            onClick={() => {
                              setReassignError(null);
                              setToUserId("");
                              setDeactivate(true);
                              setRevokeTokens(true);
                              setOffboard(u);
                            }}
                          >
                            Offboard
                          </Button>
                        )}
                      </div>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </div>
      </div>

      {/* Add user */}
      <BulkModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add user"
        description="Pre-provision a user with a role. They sign in with Microsoft and keep this role."
        confirmLabel="Create user"
        confirmDisabled={!addForm.email.trim() || !addForm.name.trim()}
        busy={createUser.isPending}
        error={addError}
        onConfirm={() => createUser.mutate()}
      >
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="nu-email">Email</Label>
            <Input id="nu-email" type="email" placeholder="person@cbcinc.ai" value={addForm.email}
              onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="nu-name">Name</Label>
            <Input id="nu-name" placeholder="Full name" value={addForm.name}
              onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="nu-role">Role</Label>
              <Select id="nu-role" value={addForm.role} onChange={(e) => setAddForm((f) => ({ ...f, role: e.target.value }))}>
                {ROLES.map((r) => <option key={r} value={r}>{pretty(r)}</option>)}
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="nu-entity">Entity</Label>
              <Select id="nu-entity" value={addForm.entity} onChange={(e) => setAddForm((f) => ({ ...f, entity: e.target.value }))}>
                {ENTITIES.map((en) => <option key={en} value={en}>{en}</option>)}
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="nu-territory">Territory (optional)</Label>
            <Input id="nu-territory" value={addForm.territory}
              onChange={(e) => setAddForm((f) => ({ ...f, territory: e.target.value }))} />
          </div>
        </div>
      </BulkModal>

      {/* Offboard / reassign */}
      <BulkModal
        open={!!offboard}
        onClose={() => setOffboard(null)}
        title={`Offboard ${offboard?.name ?? ""}`}
        description="Reassign everything this user owns to a successor. Authored history (activities, notes) stays attributed to them."
        confirmLabel="Reassign & offboard"
        confirmDanger
        confirmDisabled={!toUserId}
        busy={reassign.isPending}
        error={reassignError}
        onConfirm={() => reassign.mutate()}
      >
        {offboard && (
          <div className="space-y-3">
            <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
              Owns: <b>{offboard.counts.accounts}</b> accounts · <b>{offboard.counts.contacts}</b> contacts ·{" "}
              <b>{offboard.counts.leads}</b> leads · <b>{offboard.counts.deals}</b> deals ·{" "}
              <b>{offboard.counts.jobOrders}</b> job orders · <b>{offboard.counts.candidates}</b> candidates
            </div>
            <div className="space-y-1">
              <Label htmlFor="ob-to">Reassign all owned records to</Label>
              <Select id="ob-to" value={toUserId} onChange={(e) => setToUserId(e.target.value)}>
                <option value="">Select a user…</option>
                {reassignTargets.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={deactivate} onChange={(e) => setDeactivate(e.target.checked)} />
              Deactivate this user (they can no longer sign in)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={revokeTokens} onChange={(e) => setRevokeTokens(e.target.checked)} />
              Revoke their email access (delete stored Microsoft tokens)
            </label>
          </div>
        )}
      </BulkModal>
    </>
  );
}
